import { Box, Button, Divider, Group } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { Link } from "gatsby";
import React, { useEffect, useState } from "react";

import { Card, CardProps, ContactUsDrawer, MegaMenu } from "@components";

import { MenuStructure } from "@types";

import logoHorizontalBlack from "../../images/logo-horizontal-black.svg";
import logoHorizontal from "../../images/logo-horizontal.svg";
import * as classes from "./Header.module.css";

export interface HeaderProps extends CardProps {
  menuStructure: MenuStructure;
  light?: boolean;
}

export function Header(props: HeaderProps) {
  const { menuStructure, light = false, ...cardProps } = props;
  const [scrollY, setScrollY] = useState(0);
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const [contactUsOpened, { open: openContactUs, close: closeContactUs }] =
    useDisclosure(false);

  const buttonProps = {
    variant: "transparent",
    c: light ? "brandBlack" : "brandWhite",
    fw: "600",
    size: "xs",
    h: 46,
    px: "md",
    className: classes.button,
    "data-theme": light ? "light" : "dark",
  };

  useEffect(() => {
    const hash = window.location.hash;
    if (hash === "#contact") {
      openContactUs();
    }

    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll(); // Set initial value

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  // Calculate opacity based on scroll position or active menu
  // Fully transparent at top, fully opaque after 100px scroll or when menu is active
  const scrollOpacity = Math.min(scrollY / 200, 1);
  const backgroundOpacity = activeMenu ? 1 : scrollOpacity;

  const handleMenuEnter = (menu: string) => {
    setActiveMenu(menu);
  };

  const handleMenuLeave = () => {
    setActiveMenu(null);
  };

  return (
    <>
      <Card
        {...cardProps}
        className={classes.container}
        px="lg"
        py="lg"
        style={{
          backgroundColor: light
            ? `rgba(255, 255, 255, ${backgroundOpacity * 1})`
            : `rgba(0, 0, 0, ${backgroundOpacity * 1})`,
          transition: "background-color 0.3s ease",
          boxShadow: light
            ? `0 2px 2px 0 rgba(0, 0, 0, ${backgroundOpacity * 0.05})`
            : undefined,
        }}
        c={light ? "brandBlack" : "brandWhite"}
        pos="fixed"
        top={25}
        left={25}
        right={25}
        maw={1878}
        onMouseLeave={handleMenuLeave}
      >
        <Box className={classes.navRow}>
          <Group justify="space-between" pl="xs">
            <Link to="/">
              <img
                src={light ? logoHorizontalBlack : logoHorizontal}
                alt="Airtight Design"
                width={200}
                style={{ display: "block" }}
              />
            </Link>

            <Group>
              {menuStructure.items.map((item) =>
                item.submenu ? (
                  <Box
                    key={item.label}
                    onMouseEnter={() =>
                      handleMenuEnter(item.label.toLowerCase())
                    }
                    className={classes.menuItem}
                  >
                    <Button
                      {...buttonProps}
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      component={item.href ? (Link as any) : "button"}
                      to={item.href}
                    >
                      {item.label}
                    </Button>
                  </Box>
                ) : (
                  <Button
                    key={item.label}
                    {...buttonProps}
                    component={Link}
                    to={item.href!}
                    onMouseEnter={handleMenuLeave}
                  >
                    {item.label}
                  </Button>
                )
              )}

              <Button
                {...buttonProps}
                className={undefined}
                ml="md"
                variant="glass-square"
                color="brandPrimary"
                c={light ? "brandWhite" : undefined}
                leftSection={<Box mt={2}>{menuStructure.cta.icon}</Box>}
                onClick={openContactUs}
              >
                {menuStructure.cta.label}
              </Button>
            </Group>
          </Group>

          <Divider
            color={light ? "rgba(0, 0, 0, 0.1)" : "rgba(255, 255, 255, 0.1)"}
            className={classes.megaMenuDivider}
            style={{
              opacity: activeMenu ? 1 : 0,
            }}
          />
        </Box>

        <Box
          className={classes.megaMenuWrapper}
          style={{
            height: activeMenu ? "380px" : "0",
          }}
        >
          <Box
            className={classes.megaMenu}
            px="xl"
            pt="calc(var(--mantine-spacing-xl) + var(--mantine-spacing-md))"
            pb="0"
            style={{
              opacity: activeMenu ? 1 : 0,
              transition: activeMenu
                ? "opacity 0.2s ease 0.2s"
                : "opacity 0.2s ease",
            }}
          >
            {menuStructure.items.map(
              (item) =>
                item.submenu &&
                activeMenu === item.label.toLowerCase() && (
                  <MegaMenu
                    key={item.label}
                    title={item.submenu.title}
                    content={item.submenu.content}
                    cta={item.submenu.cta}
                    links={item.submenu.links}
                    light={light}
                  />
                )
            )}
          </Box>
        </Box>
      </Card>

      <ContactUsDrawer
        opened={contactUsOpened}
        onClose={closeContactUs}
        onCloseStack={closeContactUs}
      />
    </>
  );
}
